import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button } from '@img/ui';
import { api, type Paginated } from '../lib/api';
import { SEO } from '../components/SEO';

interface AuctionListItem {
  id: string;
  title: string;
  city: string | null;
  location: string | null;
  coverImage: string | null;
  openingPrice: number | string;
  currentBid: number | string;
  startAt: string;
  endAt: string;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED' | 'COMPLETED';
  totalBids: number;
  property: { id: string; title: string; coverImage: string | null } | null;
}

export function AuctionsPage() {
  const { data: liveData, isLoading: loadingLive }   = useQuery({
    queryKey: ['auctions', 'LIVE'],
    queryFn: () => api.get<Paginated<AuctionListItem>>('/api/auctions', { query: { status: 'LIVE' } }),
  });
  const { data: schedData } = useQuery({
    queryKey: ['auctions', 'SCHEDULED'],
    queryFn: () => api.get<Paginated<AuctionListItem>>('/api/auctions', { query: { status: 'SCHEDULED' } }),
  });
  const { data: endedData }     = useQuery({
    queryKey: ['auctions', 'ENDED'],
    queryFn: () => api.get<Paginated<AuctionListItem>>('/api/auctions', { query: { status: 'ENDED', limit: 6 } }),
  });
  const { data: completedData } = useQuery({
    queryKey: ['auctions', 'COMPLETED'],
    queryFn: () => api.get<Paginated<AuctionListItem>>('/api/auctions', { query: { status: 'COMPLETED', limit: 6 } }),
  });
  // اجمع المنتهية والمكتملة (المباعة) وفسّر بالأحدث
  const finishedAuctions = [
    ...(completedData?.data ?? []),
    ...(endedData?.data ?? []),
  ].sort((a, b) => +new Date(b.endAt) - +new Date(a.endAt));

  return (
    <div className="theme-auctions max-w-7xl mx-auto px-4 py-8">
      <SEO
        title="مزادات عقارية مباشرة في السعودية 🔨"
        description="أكبر منصة مزادات عقارية إلكترونية في المملكة العربية السعودية. مزادات مباشرة على الفلل والأراضي والشقق والعمارات والمزارع. مزادات معتمدة من الهيئة العامة للعقار (REGA)."
        keywords="مزادات عقارية, مزاد عقاري, مزاد على الهواء, مزادات السعودية, مزاد الكتروني, البورصة العقارية, صورة المزادات, مزاد فلل, مزاد أراضي, مزاد شقق, مزاد عمارات, مزاد علني, مزادات الرياض, مزادات جدة, مزادات الدمام, تسجيل مزاد, محرّج المزاد, تأمين المزاد, مزاد مباشر, طلب إقامة مزاد, شركة صورة العقارية, IMG, img.sa"
        canonical="https://img.sa/auctions"
        jsonLd={{
          '@type': 'CollectionPage',
          name: 'مزادات عقارية مباشرة',
          description: 'منصة المزادات العقارية الإلكترونية في السعودية',
          url: 'https://img.sa/auctions',
          about: {
            '@type': 'Service',
            name: 'مزادات عقارية إلكترونية',
            serviceType: 'Real Estate Auction',
            areaServed: { '@type': 'Country', name: 'Saudi Arabia' },
          },
        }}
      />
      <div className="flex items-start justify-between gap-3 flex-wrap mb-8">
        <div>
          <h1 className="text-3xl font-black text-navy-dark mb-2 flex items-center gap-2">
            🔨 المزادات العقارية
          </h1>
          <p className="text-ink-secondary">شارك في المزادات الحية وكن أول من يحصل على أفضل الفرص</p>
        </div>
        <Link to="/request-auction">
          <Button variant="gold" size="lg">🔨 اطلب مزاد على عقارك</Button>
        </Link>
      </div>

      {/* 🔴 LIVE */}
      <Section
        title="مباشر الآن 🔴"
        emoji="🔴"
        color="red"
        items={liveData?.data}
        loading={loadingLive}
        emptyText="لا توجد مزادات مباشرة حالياً"
      />

      {/* ⏰ Upcoming */}
      <Section
        title="قادمة قريباً ⏰"
        emoji="⏰"
        color="navy"
        items={schedData?.data}
        emptyText="لا توجد مزادات قادمة"
      />

      {/* ✓ المنتهية / المباعة (تظهر دائماً) */}
      <Section
        title="مزادات منتهية"
        emoji="✓"
        color="gray"
        items={finishedAuctions}
        emptyText="لا توجد مزادات منتهية بعد"
      />
    </div>
  );
}

function Section({
  title, items, loading, emptyText, color,
}: {
  title: string;
  emoji: string;
  color: 'red' | 'navy' | 'gray';
  items?: AuctionListItem[];
  loading?: boolean;
  emptyText?: string;
}) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-black text-navy-dark mb-4 border-r-4 border-gold pr-3">{title}</h2>
      {loading ? (
        <div className="text-center py-8 text-gray-500">⟳ جاري التحميل…</div>
      ) : !items?.length ? (
        emptyText && (
          <Card>
            <CardBody className="text-center py-8 text-gray-500">{emptyText}</CardBody>
          </Card>
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((a) => <AuctionCard key={a.id} auction={a} liveColor={color === 'red'} />)}
        </div>
      )}
    </section>
  );
}

function AuctionCard({ auction: a, liveColor }: { auction: AuctionListItem; liveColor?: boolean }) {
  const cover = a.coverImage || a.property?.coverImage;
  const start = new Date(a.startAt);
  const end = new Date(a.endAt);
  const isLive = a.status === 'LIVE';

  return (
    <Card hover>
      <div className="h-44 bg-gradient-to-br from-navy to-navy-dark relative">
        {cover ? (
          <img src={cover} alt={a.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white text-5xl">🔨</div>
        )}
        <div className="absolute top-3 right-3">
          {isLive && (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1 animate-pulse" style={{ background: 'var(--status-auction)', color: '#fff' }}>
              <span className="inline-block w-2 h-2 rounded-full bg-white"></span>
              مباشر الآن
            </span>
          )}
          {a.status === 'SCHEDULED' && <Badge color="navy">قادم</Badge>}
          {a.status === 'ENDED' && <Badge color="gray">منتهي</Badge>}
          {a.status === 'COMPLETED' && <Badge color="green">مكتمل</Badge>}
        </div>
      </div>
      <CardBody>
        <h3 className="font-black text-ink text-base mb-1 line-clamp-1">{a.title}</h3>
        <p className="text-xs text-gray-500 mb-3">📍 {a.city ?? a.location ?? '—'}</p>
        <div className="space-y-1 mb-4 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">{isLive ? 'المزايدة الحالية' : 'سعر الافتتاح'}:</span>
            <span className="font-black text-navy-dark">
              {Number(isLive ? a.currentBid : a.openingPrice).toLocaleString('en-US')} ر.س
            </span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>عدد المزايدات: {a.totalBids}</span>
            <span>{isLive ? `ينتهي: ${end.toLocaleDateString('ar-SA')}` : `يبدأ: ${start.toLocaleDateString('ar-SA')}`}</span>
          </div>
        </div>
        <Link to={`/auctions/${a.id}`}>
          <Button variant={liveColor ? 'danger' : 'primary'} size="sm" className="w-full">
            {isLive ? 'دخول المزاد' : 'تابع'}
          </Button>
        </Link>
      </CardBody>
    </Card>
  );
}
